import React, { useState, useEffect } from "react";
import SEO from "../seo";

const DiscountCodesInXPage = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "why-use-discount-codes",
        "how-discount-codes-work",
        "how-to-create",
        "best-practices",
        "important-considerations",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Discount Codes in XPage | xPage Help Center"
        description="Learn how to create and manage discount codes in XPage. Create targeted promotions for email marketing, social media, and campaigns with automatic or custom discount codes."
        keywords="xPage, discount codes, promotions, marketing campaigns, discount creation, automatic discounts, manual discounts, checkout promotion, ecommerce marketing"
        canonical="https://help.xpage.ai/help/business-expansion/%20Discount%20Codes%20in%20XPage%20"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Discount Codes in XPage
          </h1>
          <p className="text-sm text-systemLabel">
            Discount codes in XPage allow merchants to create targeted
            promotions that customers can apply at checkout. XPage supports two
            discount types: Automatic discounts and Discount codes (manual).
            Discount codes are ideal for controlled marketing campaigns where
            you want to track performance and distribute offers selectively.
          </p>
        </header>

        {/* Why Use Discount Codes Section */}
        <section id="why-use-discount-codes" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Why Use Discount Codes in XPage?
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Using discount codes gives you advanced control over your
              promotions:
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Targeted Marketing Control
            </h3>
            <p className="text-sm text-systemLabel mb-4">
              Discount codes are perfect for:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>Email marketing campaigns</li>
              <li>Social media promotions</li>
              <li>Influencer and affiliate partnerships</li>
              <li>Offline advertising and special events</li>
            </ul>
            <p className="text-sm text-systemLabel mb-4">
              Each code can be shared only with specific audiences, ensuring
              precise discount distribution.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Promotion Performance Tracking
            </h3>
            <p className="text-sm text-systemLabel">
              Each discount code allows you to track usage and conversion,
              helping you evaluate campaign performance and optimize your
              marketing strategy.
            </p>
          </div>
        </section>

        {/* How Discount Codes Work Section */}
        <section id="how-discount-codes-work" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How Discount Codes Work
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              After creating a discount code:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mb-4">
              <li>It appears in your Discounts list in XPage Admin</li>
              <li>You can share it via email, ads, or any external channel</li>
              <li>
                Customers enter the code during checkout to redeem the discount
              </li>
            </ul>
          </div>
        </section>

        {/* How to Create Section */}
        <section id="how-to-create" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to Create a Discount Code in XPage
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-2 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your XPage Admin Dashboard
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to Offers → Discounts
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Create discount</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Choose the discount type:</span>
                <ul className="list-disc list-inside space-y-1 ml-6 mt-1">
                  <li>Percentage</li>
                  <li>Fixed Amount</li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">Select Discount code</span>
                <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-2">
                  <p className="text-sm text-systemLabel">
                    Choose to generate a random code automatically, or create a
                    custom discount code.
                  </p>
                  <p className="text-sm text-systemLabel mt-1 font-medium">
                    Important: Each discount code must be globally unique across
                    XPage.
                  </p>
                </div>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Configure the discount settings:
                </span>
                <ul className="list-disc list-inside space-y-1 ml-6 mt-1">
                  <li>Eligible products or collections</li>
                  <li>Discount value</li>
                  <li>Discount combinations</li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save</span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mt-4">
              <p className="text-sm text-systemLabel">
                You can copy the discount code directly from the Discounts list
                or from the Overview section of the discount details page.
              </p>
            </div>
          </div>
        </section>

        {/* Best Practices Section */}
        <section id="best-practices" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Best Practices for Discount Codes
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>Use simple alphanumeric codes for easy customer entry</li>
              <li>Avoid special characters and symbols</li>
              <li>Keep codes short and readable for better conversion</li>
            </ul>
          </div>
        </section>

        {/* Important Considerations Section */}
        <section id="important-considerations" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Important Considerations
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Time Zone Settings
            </h3>
            <p className="text-sm text-systemLabel mb-4">
              Discount start and end times follow your store's configured time
              zone.
            </p>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md mb-4">
              <p className="text-sm text-systemLabel">
                Example: If your store is set to GMT and a discount starts on
                March 15, it will activate at 12:00 AM GMT on March 15.
              </p>
            </div>

            <p className="text-sm text-systemLabel">
              Always verify your store time zone to ensure discounts activate at
              the intended time.
            </p>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            {[
              { id: "overview", label: "Overview" },
              { id: "why-use-discount-codes", label: "Why Use Discount Codes" },
              {
                id: "how-discount-codes-work",
                label: "How Discount Codes Work",
              },
              { id: "how-to-create", label: "How to Create" },
              { id: "best-practices", label: "Best Practices" },
              {
                id: "important-considerations",
                label: "Important Considerations",
              },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`block py-1 transition-colors border-l-2 pl-3 ${
                    activeSection === item.id
                      ? "text-deepPurple font-semibold border-deepPurple"
                      : "border-transparent hover:text-deepPurple"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DiscountCodesInXPage;
